import { gql } from "graphql-tag"

const typeOrder = gql`
  type Order {
    id: String
    version: Int
    createdAt: DateTime
    createdBy: JSON
    lastModifiedAt: DateTime
    lastModifiedBy: JSON
    completedAt: DateTime
    orderNumber: String
    customerId: String
    customerEmail: String
    anonymousId: String
    businessUnit: JSON
    store: JSON
    lineItems: [LineItem]
    customLineItems: [CustomLineItem]
    totalPrice: JSON
    taxedPrice: JSON
    taxedShippingPrice: JSON
    shippingAddress: JSON
    billingAddress: JSON
    shippingMode: String
    shippingKey: String
    shippingCustomFields: JSON
    shipping: [JSONObject]
    taxMode: String
    taxRoundingMode: String
    taxCalculationMode: String
    customerGroup: JSON
    country: String
    orderState: String
    state: JSON
    shipmentState: String
    paymentState: String
    shippingInfo: JSON
    syncInfo: JSON
    returnInfo: JSON
    purchaseOrderNumber: String
    discountCodes: [JSONObject]
    directDiscounts: [JSONObject]
    refusedGifts: [JSONObject]
    cart: JSON
    quote: JSON
    custom: JSON
    paymentInfo: JSON
    locale: String
    inventoryMode: String
    shippingRateInput: JSON
    origin: String
    itemShippingAddresses: [JSONObject]
    specialOrderItem: Boolean
  }

  type CustomerOrder {
    admNumber: String
    approvalDate: String
    branchNumber: String
    customerBranchNumber: String
    customerNumber: String
    companyName1: String
    companyName2: String
    customerCompanyName: String
    customerOrderText: String
    orderDate: String
    orderNumberLong: String
    orderNumberShort: String
    orderStatus: String
    orderType: String
    paymentMethod: String
    totalAmount: Float
    returnedPalletAmount: Float
    picklist: String
  }

  type OrderItem {
    positionId: String
    sortNumber: Int
    totalFreightWeight: Float
    totalPrice: Float
    quantity: Int
    price: Float
    itemStatus: String
    freightWeight: Float
    amount: Float
    article: Article
    discount: Float
    description: String
    additionalDescription: String
    sellableInDecimals: Boolean
    posId: String
  }

  type DraftItem {
    additionalDescription: String
    amount: Float
    contents: String
    container: String
    description: String
    discount: Float
    posId: String
    price: Float
    quantity: Float
    sku: String
    sortNumber: Int
    totalPrice: Float
  }

  type LongCustomerOrder {
    orderNumberLong: String
    orderNumberShort: String
    orderType: String
    customer: Customer
    admUser: Customer
    employeeUser: Employee
    orderDate: String
    shippingDate: String
    deliveryMethod: String
    totalFreightWeight: Float
    orderStatus: String
    customerBranch: BranchOffice
    branch: BranchOffice
    customerOrderNumber: String
    customerOrderText: String
    costUnit: String
    city: String
    companyName1: String
    companyName2: String
    country: String
    deliveryMethodDescription: String
    discount: Float
    discountKey: String
    employeeNumber: String
    infoText1: String
    infoText2: String
    infoText3: String
    otherCosts: Float
    packagingCosts: Float
    paymentMethod: String
    paymentTill: String
    postalCode: String
    shippingAdditionalStreetInfo: String
    shippingAddress1: String
    shippingAddress2: String
    shippingCity: String
    shippingCosts: Float
    shippingCountry: String
    shippingPostalCode: String
    shippingStreet: String
    street: String
    totalAmount: Float
    vatKey: String
    valueOfGoods: Float
    discountAmount: Float
    discountPercent: Float
    grossInvoiceAmount: Float
    netInvoiceAmount: Float
    vatPercent: Float
    vatAmount: Float
    validTill: String
    sokoDiscountValue: Float
    sokoDiscountPercent: Int
    sokoPrice: Float
    picklist: String
    groupBillingKey: String
    items: [OrderItem]
    draftItems: [DraftItem]
  }

  type Employee {
    name: String
    number: String
  }

  type CustomerOrders {
    items: [CustomerOrder]
    page: Int
    size: Int
    total: Int
    totalPages: Int
  }

  type Orders {
    limit: Int
    offset: Int
    count: Int
    total: Int

    items: [Order]
  }

  type OrderReturnItem {
    number: String
    orderNumber: String
    positionId: String
    sku: String
    value: Float
  }

  type OrderReturn {
    customerNumber: String
    customerId: String
    returnItems: [OrderReturnItem]
  }

  type OrderPrevNext {
    previousOrderNumber: String
    nextOrderNumber: String
  }

  type PaletteOrder {
    customerNumber: String
    shortOrderNumber: String
    orderNumber: String
    amount: Float
    returnedAmount: Float
  }

  type Palette {
    total: Float
    returned: Float
    inPossession: Float
    difference: Float
    returnedOrders: [PaletteOrder]
    inPossessionOrders: [PaletteOrder]
  }

  input ReservationArticleData {
    number: String!
    amount: Float!
    position: String!
    posId: String!
  }

  input OrdersFilterValues {
    field: String!
    operator: String!
    value: String!
  }

  input DeliveryObjectDetails {
    object: String
    name: String
    companyName: String
    street: String
    country: String
    postalCode: String
    city: String
    additionalLocation: String
  }

  input OrderCreateValues {
    addressId: String
    shippingMethodName: String!
    contactPersons: [String]
    sendInfoMail: Boolean = true
    collectiveInvoiceType: String!
    documentType: String!
    admNumber: String!
    validTill: String
    deliveryDate: String
    paymentTill: String
    additionalInformationOne: String
    additionalInformationTwo: String
    additionalInformationThree: String
    emailMessage: String
    emailSubject: String
    deliveryObjectValues: DeliveryObjectDetails
    branchId: String!
    orderText: String
    orderNumber: String
    displayRecommendedSalesPrice: Boolean
    printOffer: Boolean
    costUnit: String
    togglePicklist: Boolean = true
  }

  input OrderCancelValues {
    reason: String!
    branchId: String!
  }

  input UpdateReservationMutationValues {
    validTill: String!
    articleData: [ReservationArticleData]
    hasDraftItems: Boolean
  }

  input UpdatePicklistMutationValues {
    number: String!
    amount: Float!
    posId: String!
    createAdditionalDelivery: Boolean = false
  }

  input returnItem {
    sku: String!
    number: String!
    orderNumber: String!
    positionId: String!
    value: Float!
  }

  type Query {
    # from graphql endpoint
    orderReturnsByOrderNumbers(
      orderNumbers: [String!]!
      isCashReturn: Boolean = false
      lang: String = ""
      cdn: Boolean = true
    ): [OrderReturn]

    customerOrderByNumber(
      orderNumber: String!
      lang: String = ""
      cdn: Boolean = true
    ): LongCustomerOrder

    customerOrdersByNumber(
      orderNumbers: [String!]!
      lang: String = ""
      cdn: Boolean = true
    ): [LongCustomerOrder]

    findPrevNextOrderForAdm(
      admNumber: String!
      currentOrderNumber: String!
      orderType: String!
      lang: String = ""
      cdn: Boolean = true
    ): OrderPrevNext

    findPrevNextOrderForCustomer(
      customerNumber: String!
      currentOrderNumber: String!
      filters: [OrdersFilterValues]
      orderType: String!
      lang: String = ""
      cdn: Boolean = true
    ): OrderPrevNext

    customerOrders(
      customerNumber: String!
      page: Int = 0
      size: Int = 5
      sort: Sort = { orderBy: "orderDate", direction: "desc" }
      filterValues: [OrdersFilterValues]
      orderText: String
      searchText: String
    ): CustomerOrders

    admOrders(
      admNumber: String!
      page: Int = 0
      size: Int = 5
      sort: Sort = { orderBy: "orderDate", direction: "desc" }
      filterValues: [OrdersFilterValues]
    ): CustomerOrders

    idmOrders(
      branchId: String!
      page: Int = 0
      size: Int = 5
      sort: Sort = { orderBy: "orderDate", direction: "desc" }
      filterValues: [OrdersFilterValues]
      searchText: String
      internalOrder: Boolean
    ): CustomerOrders

    customerOrder(orderNumber: String!): CustomerOrder
    orderReturn(
      orderId: ID!
      isCashReturn: Boolean = false
      lang: String = ""
      cdn: Boolean = false
    ): Order

    ordersPaletteStats(customerNumber: String!, year: String): Palette
  }

  type Mutation {
    createOrderFromCustomerCart(cartId: String, cartKey: String, values: OrderCreateValues!): Order
    updateReservation(
      orderNumber: String!
      cdn: Boolean = true
      values: UpdateReservationMutationValues!
    ): Order
    updatePicklist(
      orderNumber: String!
      branchId: String!
      cdn: Boolean = true
      articleData: [UpdatePicklistMutationValues!]!
    ): Order
    createOrderReturn(
      returnItems: [returnItem!]!
      values: OrderCreateValues!
      customerNumber: String
    ): Order
    cancelDocument(
      orderNumber: String!
      values: OrderCancelValues!
      lang: String = ""
      cdn: Boolean = true
    ): Order
  }
`

export { typeOrder }

export default typeOrder
